import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiExternalLink, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { FiFacebook, FiInstagram } from 'react-icons/fi';
import {
  getCollections, addCollection, updateCollection, deleteCollection,
  getOrders, updateOrderStatus,
  getUsers, getMetaSettings, updateMetaSettings, getSiteSettings, updateSiteSettings
} from '../../firebase/database';
import { formatPrice, formatDate, STATUS_COLORS, ORDER_STATUSES } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

// ─── Categories ───────────────────────────────────────────────────────────────
export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', image: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCats(); }, []);
  const loadCats = async () => { setLoading(true); const c = await getCollections(); setCategories(c); setLoading(false); };

  const openAdd = () => { setEditing(null); setForm({ name: '', slug: '', description: '', image: '' }); setModal(true); };
  const openEdit = (cat) => { setEditing(cat.id); setForm(cat); setModal(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const data = { ...form, slug: form.slug || form.name.toLowerCase().replace(/\s+/g, '-') };
      if (editing) { await updateCollection(editing, data); toast.success('Updated!'); }
      else { await addCollection(data); toast.success('Added!'); }
      setModal(false); loadCats();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!id) { toast.error('Invalid item — cannot delete'); return; }
    if (!window.confirm('Delete this category?')) return;
    try {
      await deleteCollection(id);
      toast.success('Deleted');
      loadCats();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete: ' + (err.message || 'Check your admin permissions.'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-black text-gray-900">Categories</h1><p className="text-gray-500 text-sm">{categories.length} categories</p></div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2 text-sm"><FiPlus className="w-4 h-4" /> Add Category</button>
      </div>
      <div className="bg-white shadow-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Name', 'Slug', 'Description', 'Actions'].map(h => <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map(cat => (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 font-semibold">{cat.name}</td>
                  <td className="py-3 px-4 text-gray-500 font-mono text-xs">{cat.slug}</td>
                  <td className="py-3 px-4 text-gray-500">{cat.description || '—'}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(cat)} className="p-1.5 text-[#1a3a6e] hover:bg-[#1a3a6e]/10 rounded"><FiEdit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(cat.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><FiTrash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-gray-400">No categories yet</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <AnimatePresence>
        {modal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white w-full max-w-md p-6 shadow-2xl">
                <div className="flex justify-between mb-5"><h2 className="font-bold text-lg">{editing ? 'Edit' : 'Add'} Category</h2><button onClick={() => setModal(false)}><FiX className="w-5 h-5" /></button></div>
                <div className="space-y-4">
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className="input-field" placeholder="Category name" /></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Slug</label><input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="input-field" placeholder="category-slug" /></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Description</label><input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input-field" placeholder="Short description" /></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Image URL</label><input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} className="input-field" placeholder="https://..." /></div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setModal(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">{saving && <Spinner size="sm" color="white" />}{editing ? 'Update' : 'Add'}</button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Orders ───────────────────────────────────────────────────────────────────
export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => { getOrders().then(setOrders).finally(() => setLoading(false)); }, []);

  const handleStatusUpdate = async (orderId, status) => {
    setUpdatingStatus(true);
    try {
      await updateOrderStatus(orderId, status);
      setOrders(o => o.map(x => x.id === orderId ? { ...x, status } : x));
      if (selectedOrder?.id === orderId) setSelectedOrder(o => ({ ...o, status }));
      toast.success('Status updated!');
    } catch { toast.error('Failed'); } finally { setUpdatingStatus(false); }
  };

  const filtered = orders.filter(o => {
    const matchSearch = !search || o.customerName?.toLowerCase().includes(search.toLowerCase()) || o.orderId?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-black text-gray-900">Orders</h1><p className="text-gray-500 text-sm">{orders.length} total orders</p></div>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative"><FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" /><input value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-52" placeholder="Search orders..." /></div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-36">
          <option value="all">All Status</option>
          {ORDER_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white shadow-card overflow-hidden">
          {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Order', 'Customer', 'Total', 'Status', 'Date'].map(h => <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(order => (
                    <tr key={order.id} onClick={() => setSelectedOrder(order)} className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedOrder?.id === order.id ? 'bg-[#1a3a6e]/5' : ''}`}>
                      <td className="py-3 px-4 font-mono text-xs font-bold text-[#1a3a6e]">#{order.orderId || order.id?.slice(0, 8).toUpperCase()}</td>
                      <td className="py-3 px-4 font-medium">{order.customerName || 'Guest'}</td>
                      <td className="py-3 px-4 font-bold">{formatPrice(order.total || 0)}</td>
                      <td className="py-3 px-4"><span className={`text-xs font-semibold px-2 py-1 rounded-full capitalize ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>{order.status}</span></td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(order.createdAt)}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-gray-400">No orders found</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Order Detail */}
        <div className="lg:col-span-2">
          {selectedOrder ? (
            <div className="bg-white shadow-card p-5 sticky top-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-900">Order #{selectedOrder.orderId || selectedOrder.id?.slice(0,8).toUpperCase()}</h3>
                  <p className="text-xs text-gray-500">{formatDate(selectedOrder.createdAt)}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1.5 rounded-full capitalize ${STATUS_COLORS[selectedOrder.status]}`}>{selectedOrder.status}</span>
              </div>
              <div className="text-sm space-y-1.5 bg-gray-50 p-3">
                <p className="font-semibold">{selectedOrder.customerName}</p>
                <p className="text-gray-600">{selectedOrder.email}</p>
                <p className="text-gray-600">{selectedOrder.phone}</p>
                {selectedOrder.shippingAddress && <p className="text-gray-600">{selectedOrder.shippingAddress.address}, {selectedOrder.shippingAddress.city}</p>}
              </div>
              <div className="space-y-2">
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <img src={item.image || 'https://via.placeholder.com/36'} alt={item.title} className="w-9 h-9 object-cover" onError={e => e.target.src = 'https://via.placeholder.com/36'} />
                    <div className="flex-1"><p className="font-medium text-xs">{item.title}</p><p className="text-gray-400 text-xs">×{item.quantity}</p></div>
                    <p className="font-bold text-xs">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 text-sm">
                <div className="flex justify-between font-black"><span>Total</span><span className="text-[#1a3a6e]">{formatPrice(selectedOrder.total)}</span></div>
                <p className="text-xs text-gray-500 mt-1 capitalize">Payment: {selectedOrder.paymentMethod}</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Update Status</label>
                <div className="flex gap-2">
                  <select defaultValue={selectedOrder.status} onChange={e => handleStatusUpdate(selectedOrder.id, e.target.value)} className="input-field flex-1 text-xs" disabled={updatingStatus}>
                    {ORDER_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select>
                  {updatingStatus && <Spinner size="sm" />}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow-card p-10 text-center text-gray-400">
              <FiExternalLink className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">Select an order to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Customers ────────────────────────────────────────────────────────────────
export const AdminCustomers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { getUsers().then(setUsers).finally(() => setLoading(false)); }, []);

  const filtered = users.filter(u => u.role !== 'admin').filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-black text-gray-900">Customers</h1><p className="text-gray-500 text-sm">{filtered.length} customers</p></div>
      </div>
      <div className="relative mb-5 max-w-xs"><FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" /><input value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9" placeholder="Search customers..." /></div>
      <div className="bg-white shadow-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Customer', 'Email', 'Joined', 'Orders', 'Role'].map(h => <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#1a3a6e] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                      <span className="font-medium">{u.name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-500">{u.email}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                  <td className="py-3 px-4 text-center">{u.orders?.length || 0}</td>
                  <td className="py-3 px-4"><span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full capitalize">{u.role}</span></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-gray-400">No customers found</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Meta / Facebook Integration ─────────────────────────────────────────────
export const AdminMeta = () => {
  const [settings, setSettings] = useState({ pixelId: '', pageId: '', pageAccessToken: '', catalogId: '', instagramId: '', enabled: false, feedUrl: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testFired, setTestFired] = useState(false);

  useEffect(() => { getMetaSettings().then(s => { if (s) setSettings(prev => ({ ...prev, ...s })); }).finally(() => setLoading(false)); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMetaSettings(settings);
      toast.success('Meta settings saved!');
      if (settings.enabled && settings.pixelId) {
        const { initPixel } = await import('../../utils/pixel');
        initPixel(settings.pixelId);
        toast.success('Pixel loaded — events will now fire on this site.');
      }
    }
    catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleTestEvent = async () => {
    if (!settings.enabled || !settings.pixelId) { toast.error('Save and enable the Pixel first'); return; }
    const { initPixel, trackPageView, isPixelReady } = await import('../../utils/pixel');
    initPixel(settings.pixelId);
    setTimeout(() => {
      trackPageView();
      setTestFired(true);
      if (isPixelReady()) toast.success('Test PageView event fired! Check Events Manager → Test Events.');
      else toast.error('Pixel script did not load. Check the Pixel ID and your network/ad-blocker.');
    }, 400);
  };

  const isReallyActive = settings.enabled && !!settings.pixelId;

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-black text-gray-900">Meta / Facebook Integration</h1>
        <p className="text-gray-500 text-sm mt-1">Connect your Facebook Page, Instagram, and Meta Pixel</p>
      </div>

      {/* Status Banner */}
      <div className={`p-4 mb-6 flex items-center gap-3 ${isReallyActive ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className={`w-3 h-3 rounded-full ${isReallyActive ? 'bg-green-500' : 'bg-yellow-500'}`} />
        <p className="text-sm font-medium">
          {isReallyActive
            ? 'Pixel is configured — PageView, ViewContent, AddToCart, InitiateCheckout, and Purchase events fire automatically on this site.'
            : 'Add your Pixel ID below, enable the toggle, and save to start tracking.'}
        </p>
      </div>

      <div className="space-y-5">
        {/* Meta Pixel — the one piece that's actually live on the storefront */}
        <div className="bg-white shadow-card p-6 border-2 border-[#1a3a6e]/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1a3a6e] flex items-center justify-center"><span className="text-white font-black text-sm">px</span></div>
            <div><h3 className="font-bold text-gray-900">Meta Pixel</h3><p className="text-xs text-gray-500">Tracks ViewContent, AddToCart, InitiateCheckout & Purchase</p></div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Pixel ID</label>
            <input value={settings.pixelId} onChange={e => setSettings(s => ({ ...s, pixelId: e.target.value }))} className="input-field" placeholder="e.g. 987654321098765" />
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-500 mb-4">
            Get this from <strong>business.facebook.com/events_manager</strong> → Connect Data Sources → Web → Facebook Pixel → Create. Copy the 15–16 digit ID shown there.
          </div>
          <button onClick={handleTestEvent} className="flex items-center gap-2 text-sm font-semibold text-[#1a3a6e] hover:text-[#e63946] transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            Send Test Event
          </button>
          {testFired && <p className="text-xs text-gray-400 mt-2">Open Events Manager → your Pixel → Test Events tab to confirm it arrived.</p>}
        </div>

        {/* Facebook Page */}
        <div className="bg-white shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 flex items-center justify-center"><FiFacebook className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-bold text-gray-900">Facebook Page</h3><p className="text-xs text-gray-500">For reference — used in catalog/ad setup, not required for the Pixel</p></div>
          </div>
          <div className="space-y-3">
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Page ID</label><input value={settings.pageId} onChange={e => setSettings(s => ({ ...s, pageId: e.target.value }))} className="input-field" placeholder="e.g. 123456789012345" /></div>
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Page Access Token</label><input type="password" value={settings.pageAccessToken} onChange={e => setSettings(s => ({ ...s, pageAccessToken: e.target.value }))} className="input-field" placeholder="Your page access token" /></div>
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-white shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center"><FiInstagram className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-bold text-gray-900">Instagram Account</h3><p className="text-xs text-gray-500">Stored for reference only</p></div>
          </div>
          <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Instagram Business Account ID</label><input value={settings.instagramId} onChange={e => setSettings(s => ({ ...s, instagramId: e.target.value }))} className="input-field" placeholder="Instagram account ID" /></div>
        </div>

        {/* Facebook Catalog — real auto-sync feed, generated live from the database */}
        <div className="bg-white shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-600 flex items-center justify-center"><FiRefreshCw className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-bold text-gray-900">Product Catalog — Auto-Sync Feed</h3><p className="text-xs text-gray-500">Every product add/edit/delete is reflected automatically</p></div>
          </div>

          <div className="mb-3">
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Catalog ID (optional, for your reference)</label>
            <input value={settings.catalogId} onChange={e => setSettings(s => ({ ...s, catalogId: e.target.value }))} className="input-field" placeholder="Your catalog ID" />
          </div>

          <div className="mb-3">
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Live Feed URL</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={settings.feedUrl || 'https://us-central1-YOUR-PROJECT-ID.cloudfunctions.net/metaCatalogFeed'}
                onClick={(e) => e.target.select()}
                className="input-field text-xs font-mono bg-gray-50"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(settings.feedUrl || 'https://us-central1-YOUR-PROJECT-ID.cloudfunctions.net/metaCatalogFeed');
                  toast.success('Copied!');
                }}
                className="px-4 bg-[#1a3a6e] text-white text-xs font-bold hover:bg-[#e63946] transition-colors rounded-lg flex-shrink-0"
              >
                Copy
              </button>
            </div>
            <div className="mt-1.5">
              <label className="text-xs text-gray-400">Replace YOUR-PROJECT-ID with your real Firebase project ID, or paste the exact URL shown after you deploy (see below), then save:</label>
              <input
                value={settings.feedUrl || ''}
                onChange={e => setSettings(s => ({ ...s, feedUrl: e.target.value }))}
                className="input-field text-xs font-mono mt-1"
                placeholder="https://us-central1-your-project.cloudfunctions.net/metaCatalogFeed"
              />
            </div>
          </div>

          <details className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
            <summary className="font-bold text-gray-700 cursor-pointer">How this works — one-time setup (5 minutes)</summary>
            <ol className="list-decimal pl-4 mt-2 space-y-1.5">
              <li>This project includes a small, ready-made <code className="bg-white px-1 rounded">functions/</code> folder — it's not something you need to write, just deploy it.</li>
              <li>In your terminal: <code className="bg-white px-1 rounded">cd functions && npm install</code></li>
              <li>Then: <code className="bg-white px-1 rounded">firebase deploy --only functions</code></li>
              <li>Firebase will print a URL like <code className="bg-white px-1 rounded">https://us-central1-yourproject.cloudfunctions.net/metaCatalogFeed</code> — paste it above and save.</li>
              <li>In <strong>Meta Commerce Manager → Catalog → Data Sources → Add Items → Data Feed</strong>, paste the same URL and set the schedule to daily.</li>
              <li>Done. Every time you add, edit, or delete a product here, the feed updates instantly — Meta just needs to re-fetch it (automatically on schedule, or click "Update Now" in Commerce Manager any time).</li>
            </ol>
          </details>

          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs text-amber-700 mt-3">
            This is the only piece of this project that needs a one-time deploy step — Meta's catalog crawler reads plain CSV over HTTP and can't execute the website's JavaScript, so a live feed has to come from a small server function rather than the browser. Everything else (Pixel tracking, the whole storefront) runs exactly as before with no server needed.
          </div>
        </div>

        {/* Enable toggle */}
        <div className="bg-white shadow-card p-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <div onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))} className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 ${settings.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
              <motion.div animate={{ x: settings.enabled ? 24 : 0 }} className="w-4 h-4 bg-white rounded-full shadow" />
            </div>
            <span className="text-sm font-semibold text-gray-700">Enable Meta Pixel Tracking</span>
          </label>
        </div>

        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 text-sm py-3 px-8">
          {saving && <Spinner size="sm" color="white" />}
          {saving ? 'Saving...' : 'Save Meta Settings'}
        </button>
      </div>
    </div>
  );
};

// ─── Site Settings ─────────────────────────────────────────────────────────────
export const AdminSettings = () => {
  const [settings, setSettings] = useState({ siteName: 'SitSmart', siteTagline: 'Smart Seating For Every Home', supportEmail: 'hello@sitsmart.pk', supportPhone: '+92 300 1234567', address: 'Karachi, Sindh, Pakistan', freeShippingThreshold: 5000, currency: 'PKR' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getSiteSettings().then(s => { if (s && Object.keys(s).length) setSettings(prev => ({ ...prev, ...s })); }).finally(() => setLoading(false)); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await updateSiteSettings(settings); toast.success('Settings saved!'); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-2xl">
      <div className="mb-6"><h1 className="font-display text-2xl font-black text-gray-900">Site Settings</h1><p className="text-gray-500 text-sm">Manage general website settings</p></div>
      <div className="bg-white shadow-card p-6 space-y-5">
        <h3 className="font-bold text-gray-900 border-b pb-3">General</h3>
        {[
          { label: 'Site Name', key: 'siteName', placeholder: 'SitSmart' },
          { label: 'Tagline', key: 'siteTagline', placeholder: 'Smart Seating For Every Home' },
          { label: 'Support Email', key: 'supportEmail', placeholder: 'hello@sitsmart.pk' },
          { label: 'Support Phone', key: 'supportPhone', placeholder: '+92 300 1234567' },
          { label: 'Address', key: 'address', placeholder: 'Karachi, Pakistan' },
        ].map(({ label, key, placeholder }) => (
          <div key={key}>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">{label}</label>
            <input value={settings[key] || ''} onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))} className="input-field" placeholder={placeholder} />
          </div>
        ))}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Free Shipping Threshold (PKR)</label>
          <input type="number" value={settings.freeShippingThreshold} onChange={e => setSettings(s => ({ ...s, freeShippingThreshold: +e.target.value }))} className="input-field" />
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 text-sm mt-2">
          {saving && <Spinner size="sm" color="white" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminOrders;
